import { Autocomplete, TextField } from "@mui/material";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";

import { countryCodes, countryName } from "../../lib/i18n/countryOptions";
import { type InputProps, answerIds } from "../answers";
import { useReadOnly } from "../readOnly";

import { chipSlot, fieldSx } from "./optionSx";

export const CountryInput: React.FC<InputProps<"country">> = ({ question, answer, onChange, error }) => {
    const readOnly = useReadOnly();
    const { formatMessage, locale } = useIntl();
    const options = useMemo(
        () =>
            countryCodes()
                .map((code) => ({ code, name: countryName(code, locale) }))
                .sort((a, b) => a.name.localeCompare(b.name, locale)),
        [locale]
    );
    const label = (code: string) => options.find((o) => o.code === code)?.name ?? code;
    const placeholder = question.multi
        ? formatMessage({ defaultMessage: "Select countries" })
        : formatMessage({ defaultMessage: "Select country" });
    const input = (params: object) => <TextField {...params} size="small" error={error} placeholder={placeholder} />;
    const codes = options.map((o) => o.code);

    if (question.multi) {
        return (
            <Autocomplete
                disabled={readOnly}
                size="small"
                autoHighlight
                multiple
                disableCloseOnSelect
                options={codes}
                getOptionLabel={label}
                value={answerIds(answer?.value)}
                onChange={(_, value) => onChange({ ...answer, value })}
                renderInput={input}
                slotProps={{ chip: chipSlot }}
                sx={fieldSx}
            />
        );
    }

    return (
        <Autocomplete
            disabled={readOnly}
            size="small"
            autoHighlight
            options={codes}
            getOptionLabel={label}
            value={typeof answer?.value === "string" && answer.value ? answer.value : null}
            onChange={(_, value) => onChange({ ...answer, value: value ?? undefined })}
            renderInput={input}
            sx={fieldSx}
        />
    );
};
